import { Flex, Text, forwardRef } from '@chakra-ui/react';
import type { FlexProps } from '@chakra-ui/react';
import { useMemo } from 'react';

import {
  AssistanceIcon,
  AssistanceRejectedIcon,
} from '@frontend/components/src/components/mediaAndIcons/icons/iconSet';
import { toCamelCase } from '@frontend/utils';

import type { FieldSuggestionProvenance } from '../';

const PROVENANCE_TEXT: Record<string, string> = {
  null: '',
  filename: 'From file name ',
  content: 'Found in ',
  drawingNumber: 'From drawing number ',
  cadAttribute: 'Found in ',
};

const provenanceTextRender = (provenanceByline: [string, string, string?]) => {
  return (
    <Text size="sm" color="feature.secondary">
      {provenanceByline[0] + ' '}
      <Text
        as="span"
        size="sm"
        color="interaction.suggestion"
        wordBreak="break-all"
        whiteSpace="pre-line"
        m={0}
      >
        {provenanceByline[1].trim()}
      </Text>
      {provenanceByline[2] ? ' ' + provenanceByline[2] : ''}
    </Text>
  );
};

export const SuggestionMenuItem = forwardRef(
  (
    {
      value,
      provenance,
      isRejected,
      ...props
    }: {
      value: string;
      provenance?: FieldSuggestionProvenance[];
      isRejected?: boolean;
    } & FlexProps,
    ref,
  ) => {
    const provenanceByline: [string, string, string?] | null = useMemo(() => {
      if (!provenance || !provenance.length) return null;
      if (provenance.length === 1) {
        return [
          PROVENANCE_TEXT[toCamelCase(provenance[0].metadata?.src ?? 'null')],
          provenance[0].filename,
        ];
      }
      const moreFiles = provenance.length - 1;
      return [
        'From',
        provenance[0].filename,
        `and ${moreFiles} more file${moreFiles > 1 ? 's' : ''}`,
      ];
    }, [provenance]);

    return (
      <Flex
        ref={ref}
        cursor="pointer"
        py={0.5}
        pr={1}
        pl={2}
        h="auto"
        w="100%"
        justify="space-between"
        align="start"
        _active={{
          bg: 'interaction.surfaceSuggestionActive',
        }}
        {...props}
      >
        <Flex direction="column" align="flex-start" mr={1}>
          <Text
            wordBreak="break-all"
            whiteSpace="pre-line"
            data-fs-hide="true"
            m={0}
          >
            {value.trim()}
          </Text>
          {!isRejected &&
            provenanceByline &&
            provenanceTextRender(provenanceByline)}
        </Flex>
        {isRejected ? (
          <AssistanceRejectedIcon boxSize={3} color="feature.placeholder" />
        ) : (
          <AssistanceIcon boxSize="1.375rem" color="interaction.suggestion" />
        )}
      </Flex>
    );
  },
);
